import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { ExposedReadUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadUserDetailsRes';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { PensionService } from 'app/services/api/pension/pension.service';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { USER_ROLES } from 'common/constants/user-role.constants';

@Component({
    selector: 'pension-paycheck-runs-print',
    templateUrl: './pension-paycheck-runs-print.component.html',
    styleUrls: ['./pension-paycheck-runs-print.component.scss'],
})
export class PensionPaycheckRunsPrintComponent implements OnInit, OnDestroy {
    @ViewChild('viewerFrame') viewerFrame!: ElementRef<HTMLIFrameElement>;
    @ViewChild('printFrame') printFrame!: ElementRef<HTMLIFrameElement>;

    userRoles = USER_ROLES;
    userDetails: ExposedReadUserDetailsRes = null;
    hasPaycheckRunsRole: boolean = false;

    isLoading: boolean = true;
    fileName: string = 'launasedlar.pdf';
    errorMessage: string | null = null;

    private blobUrl: string | null = null;
    private token: string = '';

    // iframe src needs SafeResourceUrl
    pdfFrameUrl: SafeResourceUrl | null = null;

    constructor(
        private route: ActivatedRoute,
        private alertService: AlertsService,
        private pensionService: PensionService,
        private sanitizer: DomSanitizer,
        public lsrauthService: LsrauthService
    ) {
        this.lsrauthService.userDetails.subscribe((userDetails) => (this.userDetails = userDetails));
    }

    ngOnInit(): void {
        this.lsrauthService.userDetails.subscribe((u) => {
            this.userDetails = u;
            this.hasPaycheckRunsRole = this.lsrauthService.inRole(u, this.userRoles.PENSION_PAYCHECK_RUNS);
        });
        this.token = this.route.snapshot.queryParamMap.get('token') ?? '';

        if (!this.token) {
            this.isLoading = false;
            this.errorMessage = 'Vantar token í hlekk.';
            return;
        }

        this.loadPdf();
    }

    private async loadPdf() {
        this.isLoading = true;
        this.errorMessage = null;

        try {
            const res = await this.pensionService.getPostalPdfByTokenResponse(this.token);
            const blob = res.body as Blob;

            if (blob.type && !blob.type.includes('pdf')) {
                const text = await blob.text().catch(() => '');
                throw new Error(text || `Expected PDF, got ${blob.type}`);
            }

            this.revokeBlobUrl();
            this.blobUrl = URL.createObjectURL(blob);

            // iframe preview
            this.pdfFrameUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl);
        } catch (e: any) {
            console.error('Failed to load PDF', e);
            this.errorMessage = e?.message ?? 'Mistókst að hlaða PDF.';
        } finally {
            this.isLoading = false;
        }
    }

    copyLink(): void {
        navigator.clipboard?.writeText(window.location.href);
        this.alertService.changeAlertShowIcon(true);
        this.alertService.changeAlertSuccessMessage('Hlekkur afritaður.');
        this.alertService.changeAlertSuccessShow(true);
    }

    downloadPdf(): void {
        if (!this.blobUrl) return;

        const runId = this.route.snapshot.queryParamMap.get('runId');
        if (runId) this.fileName = `Launasedlar_keyrsla_${runId}.pdf`;

        const a = document.createElement('a');
        a.href = this.blobUrl;
        a.download = this.fileName;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
    }

    print(): void {
        if (!this.blobUrl) return;

        const iframe = this.printFrame?.nativeElement;
        if (!iframe) {
            window.open(this.blobUrl, '_blank', 'noopener,noreferrer');
        }

        iframe.onload = () => {
            setTimeout(() => {
                try {
                    iframe.contentWindow?.focus();
                    iframe.contentWindow?.print();
                } catch (e) {
                    console.warn('iframe print failed, fallback to new tab', e);
                    window.open(this.blobUrl!, '_blank', 'noopener,noreferrer');
                }
            }, 200);
        };

        // force reload so onload triggers reliably
        iframe.src = 'about:blank';
        setTimeout(() => (iframe.src = this.blobUrl!), 0);
    }

    openInNewTab(): void {
        if (!this.blobUrl) return;
        window.open(this.blobUrl, '_blank', 'noopener,noreferrer');
    }

    retry(): void {
        this.loadPdf();
    }

    private revokeBlobUrl(): void {
        if (this.blobUrl) {
            URL.revokeObjectURL(this.blobUrl);
            this.blobUrl = null;
        }
    }

    ngOnDestroy(): void {
        this.revokeBlobUrl();
    }
}
